import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const GetStartedContainer = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const GetStartedCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  width: 100%;
  overflow: hidden;
`;

const ProgressBar = styled.div`
  height: 4px;
  background: #E5E7EB;
  position: relative;
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background: ${props => props.theme.colors.primary};
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const StepContainer = styled.div<{ active: boolean }>`
  padding: 48px;
  display: ${props => props.active ? 'block' : 'none'};
  animation: ${props => props.active ? 'fadeIn 0.3s ease' : 'none'};
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Logo = styled.div`
  font-size: 64px;
  text-align: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  margin-bottom: 16px;
  text-align: center;
`;

const Subtitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: 16px;
`;

const Description = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: 24px;
  text-align: center;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
  margin-bottom: 48px;
`;

const Feature = styled.div`
  display: flex;
  gap: 12px;
`;

const FeatureIcon = styled.span`
  font-size: 24px;
`;

const FeatureText = styled.div`
  h4 {
    font-size: 16px;
    font-weight: 600;
    color: ${props => props.theme.colors.text};
    margin-bottom: 4px;
  }
  
  p {
    font-size: 14px;
    color: ${props => props.theme.colors.text.secondary};
  }
`;

const ChoiceCards = styled.div`
  display: grid;
  gap: 16px;
  margin-bottom: 32px;
`;

const ChoiceCard = styled.div<{ selected: boolean }>`
  padding: 24px;
  border: 2px solid ${props => props.selected ? props.theme.colors.primary : '#E5E7EB'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.selected ? 'rgba(139, 92, 246, 0.05)' : 'white'};
  position: relative;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  h3 {
    font-size: 18px;
    font-weight: 600;
    color: ${props => props.theme.colors.text};
    margin-bottom: 8px;
  }
  
  p {
    font-size: 14px;
    color: ${props => props.theme.colors.text.secondary};
  }
`;

const Badge = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  background: ${props => props.theme.colors.primary};
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  
  label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: ${props => props.theme.colors.text};
    margin-bottom: 8px;
  }
  
  input, select {
    width: 100%;
    padding: 12px 16px;
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    font-size: 16px;
    transition: all 0.2s;
    
    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
      box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
    }
  }
  
  .helper-text {
    font-size: 12px;
    color: ${props => props.theme.colors.text.secondary};
    margin-top: 6px;
  }
`;

const ErrorMessage = styled.div`
  background: #FEE2E2;
  color: #991B1B;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 32px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.variant === 'secondary' ? `
    background: white;
    color: ${props.theme.colors.text.secondary};
    border: 1px solid #E5E7EB;
    
    &:hover {
      background: #F9FAFB;
    }
  ` : `
    background: ${props.theme.colors.primary};
    color: white;
    
    &:hover {
      background: ${props.theme.colors.primaryDark};
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(139, 92, 246, 0.25);
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: 48px;
  
  .checkmark {
    font-size: 72px;
    color: #10B981;
    margin-bottom: 24px;
  }
`;

export const GetStartedPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedMode, setSelectedMode] = useState<'local' | 'account' | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    userCategory: '',
    apiEndpoint: 'http://localhost:8017'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const totalSteps = 4;
  const progress = (currentStep / totalSteps) * 100;

  const handleNextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      setError('');
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setError('');
    }
  };

  const handleModeSelection = () => {
    if (selectedMode === 'local') {
      setCurrentStep(3); // Skip to local setup
    } else {
      handleNextStep();
    }
  };

  const handleCreateAccount = async () => {
    setError('');
    setLoading(true);

    try {
      // Create account
      const signupResponse = await api.auth.signup({
        email: formData.email,
        password: formData.password,
        sync_enabled: true,
        analytics_enabled: false
      });

      // Store tokens
      localStorage.setItem('access_token', signupResponse.access_token);
      localStorage.setItem('refresh_token', signupResponse.refresh_token);
      localStorage.setItem('user_email', formData.email);

      // Update settings if user category was selected
      if (formData.userCategory) {
        await api.auth.updateSettings({
          user_category: formData.userCategory
        });
      }

      // Mark onboarding as complete
      localStorage.setItem('onboardingCompleted', 'true');
      
      // Show success
      setCurrentStep(4);
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleLocalSetup = () => {
    // Save local settings
    localStorage.setItem('onboardingCompleted', 'true');
    localStorage.setItem('promptasaurus_mode', 'local');
    localStorage.setItem('api_endpoint', formData.apiEndpoint);
    if (formData.userCategory) {
      localStorage.setItem('user_category', formData.userCategory);
    }
    
    // Show success
    setCurrentStep(4);
  };

  const handleLogin = async () => {
    setError('');
    setLoading(true);

    try {
      // Login
      const loginResponse = await api.auth.login({
        email: formData.email,
        password: formData.password
      });

      // Store tokens
      localStorage.setItem('access_token', loginResponse.access_token);
      localStorage.setItem('refresh_token', loginResponse.refresh_token);
      localStorage.setItem('user_email', formData.email);

      // Mark onboarding as complete
      localStorage.setItem('onboardingCompleted', 'true');
      
      // Show success
      setCurrentStep(4);
      
      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to log in');
    } finally {
      setLoading(false);
    }
  };

  const completeOnboarding = () => {
    navigate('/');
  };

  return (
    <GetStartedContainer>
      <GetStartedCard>
        <ProgressBar>
          <ProgressFill progress={progress} />
        </ProgressBar>

        {/* Step 1: Welcome */}
        <StepContainer active={currentStep === 1}>
          <Logo>🦖</Logo>
          <Title>Welcome to Promptasaurus!</Title>
          <Description>
            Transform your prompts into powerful instructions that get better results from ChatGPT, Claude, and other AI models.
          </Description>

          <FeaturesGrid>
            <Feature>
              <FeatureIcon>✨</FeatureIcon>
              <FeatureText>
                <h4>Smart Improvements</h4>
                <p>AI-powered enhancements</p>
              </FeatureText>
            </Feature>
            <Feature>
              <FeatureIcon>🎯</FeatureIcon>
              <FeatureText>
                <h4>Role-Based</h4>
                <p>Tailored to your needs</p>
              </FeatureText>
            </Feature>
            <Feature>
              <FeatureIcon>🔒</FeatureIcon>
              <FeatureText>
                <h4>Privacy First</h4>
                <p>Your data, your control</p>
              </FeatureText>
            </Feature>
            <Feature>
              <FeatureIcon>⚡</FeatureIcon>
              <FeatureText>
                <h4>One-Click</h4>
                <p>Instant improvements</p>
              </FeatureText>
            </Feature>
          </FeaturesGrid>

          <ButtonGroup>
            <Button onClick={handleNextStep}>Get Started</Button>
          </ButtonGroup>
        </StepContainer>

        {/* Step 2: Choose Mode */}
        <StepContainer active={currentStep === 2}>
          <Subtitle>How would you like to use Promptasaurus?</Subtitle>
          <Description>You can always change this later in settings.</Description>

          <ChoiceCards>
            <ChoiceCard 
              selected={selectedMode === 'local'}
              onClick={() => setSelectedMode('local')}
            >
              <h3>🏠 Keep Everything Local</h3>
              <p>All settings stored in your browser. No account needed. Complete privacy.</p>
            </ChoiceCard>

            <ChoiceCard 
              selected={selectedMode === 'account'}
              onClick={() => setSelectedMode('account')}
            >
              <Badge>Recommended</Badge>
              <h3>☁️ Create Free Account</h3>
              <p>Sync settings across devices. Save prompt history. Unlock advanced features.</p>
            </ChoiceCard>
          </ChoiceCards>

          <ButtonGroup>
            <Button variant="secondary" onClick={handlePreviousStep}>Back</Button>
            <Button onClick={handleModeSelection} disabled={!selectedMode}>Continue</Button>
          </ButtonGroup>
        </StepContainer>

        {/* Step 3A: Local Setup */}
        <StepContainer active={currentStep === 3 && selectedMode === 'local'}>
          <Subtitle>Local Setup</Subtitle>
          <Description>Configure your preferences. These will be stored locally in your browser.</Description>

          <FormGroup>
            <label>I primarily write prompts as a:</label>
            <select 
              value={formData.userCategory}
              onChange={(e) => setFormData({...formData, userCategory: e.target.value})}
            >
              <option value="">Select your role...</option>
              <option value="developer">Developer</option>
              <option value="business_analyst">Business Analyst</option>
              <option value="screen_writer">Screen Writer</option>
            </select>
          </FormGroup>

          <FormGroup>
            <label>API Endpoint (Optional)</label>
            <input 
              type="text" 
              value={formData.apiEndpoint}
              onChange={(e) => setFormData({...formData, apiEndpoint: e.target.value})}
              placeholder="http://localhost:8017"
            />
            <div className="helper-text">
              Leave default to use the local API server, or enter your own endpoint.
            </div>
          </FormGroup>

          <ButtonGroup>
            <Button variant="secondary" onClick={() => setCurrentStep(2)}>Back</Button>
            <Button onClick={handleLocalSetup}>Complete Setup</Button>
          </ButtonGroup>
        </StepContainer>

        {/* Step 3B: Account Creation */}
        <StepContainer active={currentStep === 3 && selectedMode === 'account'}>
          <Subtitle>Create Your Account</Subtitle>
          <Description>Join thousands of users who are writing better prompts.</Description>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <FormGroup>
            <label>Email Address</label>
            <input 
              type="email" 
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="you@example.com"
            />
          </FormGroup>

          <FormGroup>
            <label>Password</label>
            <input 
              type="password" 
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              placeholder="At least 8 characters with uppercase"
            />
          </FormGroup>

          <FormGroup>
            <label>I primarily write prompts as a:</label>
            <select 
              value={formData.userCategory}
              onChange={(e) => setFormData({...formData, userCategory: e.target.value})}
            >
              <option value="">Select your role...</option>
              <option value="developer">Developer</option>
              <option value="business_analyst">Business Analyst</option>
              <option value="screen_writer">Screen Writer</option>
            </select>
          </FormGroup>

          <ButtonGroup>
            <Button variant="secondary" onClick={handlePreviousStep}>Back</Button>
            <Button onClick={handleCreateAccount} disabled={loading}>
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </ButtonGroup>
          
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <Button 
              variant="secondary" 
              onClick={() => setCurrentStep(5)}
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              Already have an account? Log in
            </Button>
          </div>
        </StepContainer>

        {/* Step 4: Success */}
        <StepContainer active={currentStep === 4}>
          <SuccessContainer>
            <div className="checkmark">✓</div>
            <Title>You're All Set!</Title>
            <Description>
              Promptasaurus is ready to help you write better prompts. 
              Look for the ✨ Improve button on ChatGPT and Claude.
            </Description>
            <ButtonGroup>
              <Button onClick={completeOnboarding}>Go to Dashboard</Button>
            </ButtonGroup>
          </SuccessContainer>
        </StepContainer>

        {/* Step 5: Login */}
        <StepContainer active={currentStep === 5}>
          <Subtitle>Welcome Back!</Subtitle>
          <Description>Log in to your Promptasaurus account.</Description>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <FormGroup>
            <label>Email Address</label>
            <input 
              type="email" 
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="you@example.com"
            />
          </FormGroup>

          <FormGroup>
            <label>Password</label>
            <input 
              type="password" 
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              placeholder="Enter your password"
            />
          </FormGroup>

          <ButtonGroup>
            <Button variant="secondary" onClick={() => setCurrentStep(3)}>Back to Sign Up</Button>
            <Button onClick={handleLogin} disabled={loading}>
              {loading ? 'Logging in...' : 'Log In'}
            </Button>
          </ButtonGroup>
        </StepContainer>
      </GetStartedCard>
    </GetStartedContainer>
  );
};